import { checkCameraAuthenticity } from "./virtual-camera-detector.js"

/**
 * Gestor principal de cámaras con validación de autenticidad
 */
export class CameraManager {
    constructor() {
        this.currentStream = null
        this.devices = []
    }

    /**
     * Inicializa y valida las cámaras disponibles
     * @returns {Promise<{devices: MediaDeviceInfo[], virtualDevices: string[]}>}
     */
    async initializeCameras() {
        try {
            // Solicitar permisos
            await navigator.mediaDevices.getUserMedia({ video: true })

            // Obtener dispositivos
            const devices = await navigator.mediaDevices.enumerateDevices()
            this.devices = devices.filter((device) => device.kind === "videoinput")

            // Validar cada dispositivo
            const virtualDevices = []

            for (const device of this.devices) {
                const isVirtual = await this.validateDevice(device)
                if (isVirtual) {
                    virtualDevices.push(device.deviceId)
                }
            }

            return {
                devices: this.devices,
                virtualDevices,
            }
        } catch (error) {
            console.error("Error initializing cameras:", error)
            throw error
        }
    }

    /**
     * Valida si un dispositivo específico es una cámara virtual
     * @param {MediaDeviceInfo} device
     * @returns {Promise<boolean>}
     */
    async validateDevice(device) {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: device.deviceId } },
            })

            const result = await checkCameraAuthenticity(stream, device)

            // Limpiar stream
            stream.getTracks().forEach((track) => track.stop())

            return !result.isAuthentic
        } catch (error) {
            console.warn(`Error validating device ${device.deviceId}:`, error)
            return false // Asumir que es real si hay error
        }
    }

    /**
     * Selecciona y valida una cámara específica
     * @param {string} deviceId
     * @returns {Promise<{stream: MediaStream, isVirtual: boolean, details: object}>}
     */
    async selectCamera(deviceId) {
        try {
            const device = this.devices.find((d) => d.deviceId === deviceId)
            if (!device) {
                throw new Error("Device not found")
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: deviceId } },
            })

            const validation = await checkCameraAuthenticity(stream, device)

            if (!validation.isAuthentic) {
                // Detener stream si es virtual
                stream.getTracks().forEach((track) => track.stop())
                throw new Error("Virtual camera detected - access denied")
            }

            this.currentStream = stream
            return {
                stream,
                isVirtual: !validation.isAuthentic,
                details: validation,
            }
        } catch (error) {
            console.error("Error selecting camera:", error)
            throw error
        }
    }

    /**
     * Detiene el stream actual
     */
    stopCurrentStream() {
        if (this.currentStream) {
            this.currentStream.getTracks().forEach((track) => track.stop())
            this.currentStream = null
        }
    }
}
